import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

export interface DashboardKPIs {
    design_count: number;
    published_design_count: number;
    total_orders: number;
    orders_this_month: number;
    orders_last_month: number;
    revenue_this_month: number;
    revenue_last_month: number;
    aov: number;
    pending_orders: number;
    completed_orders: number;
}

export interface OrderTimeSeriesPoint {
    date: string;
    count: number;
}

export interface OrderStatusCount {
    status: string;
    count: number;
}

export interface TopSellingDesign {
    design_id: number;
    title: string;
    order_count: number;
    thumbnail_url: string | null;
}

export interface DashboardSparklines {
    orders: OrderTimeSeriesPoint[];
    revenue: Array<{ date: string; value: number }>;
}

export interface DashboardComparison {
    range_orders: number;
    range_revenue: number;
    range_aov: number;
    orders_over_time: OrderTimeSeriesPoint[];
}

export interface BrandDashboardPayload {
    kpis: DashboardKPIs;
    orders_over_time: OrderTimeSeriesPoint[];
    status_breakdown: OrderStatusCount[];
    top_designs: TopSellingDesign[];
    top_new_designs: TopSellingDesign[];
    sparklines: DashboardSparklines;
    comparison?: DashboardComparison;
}

export interface GlobalKPIs {
    total_active_brands: number;
    global_revenue: number;
    fulfillment_queue_count: number;
    failed_fulfillment_count: number;
}

export interface FulfillmentPipelineStage {
    stage_name: string;
    count: number;
    percentage: number;
}

export interface SystemHealthStatus {
    service_name: string;
    last_success_at: string | null;
    last_error_at: string | null;
    last_error_message: string | null;
}

export interface RecentOrder {
    id: number;
    order_number: number;
    order_date: string;
    order_total: number;
    fulfillment_status: string;
}

export interface RecentDesign {
    id: number;
    title: string;
    created_at: string;
    brand_id: number;
    thumbnail_url?: string | null;
}

export interface DashboardDateRange {
    startISO: string; // YYYY-MM-DDTHH:mm:ss.sssZ
    endISO: string;
    presetId: string; // "7d" | "30d" | "90d" | "ytd" | "custom"
}

// Persisted shape: only the preset choice (and custom dates if presetId === "custom").
// Preset windows are resolved to live "from now" dates on every read so a stored "30d"
// always means "the last 30 days", not the 30 days that were current when the user last visited.
interface StoredRange {
    presetId: string;
    customStartISO?: string;
    customEndISO?: string;
}

const isoNDaysAgoUtcStart = (days: number): string => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - days);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString();
};

const resolveStoredRange = (stored: StoredRange): DashboardDateRange => {
    const nowISO = new Date().toISOString();
    switch (stored.presetId) {
        case "7d":  return { startISO: isoNDaysAgoUtcStart(7),  endISO: nowISO, presetId: "7d" };
        case "30d": return { startISO: isoNDaysAgoUtcStart(30), endISO: nowISO, presetId: "30d" };
        case "90d": return { startISO: isoNDaysAgoUtcStart(90), endISO: nowISO, presetId: "90d" };
        case "ytd": {
            const now = new Date();
            const jan1 = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
            return { startISO: jan1.toISOString(), endISO: nowISO, presetId: "ytd" };
        }
        case "custom":
            return {
                startISO: stored.customStartISO || isoNDaysAgoUtcStart(30),
                endISO:   stored.customEndISO   || nowISO,
                presetId: "custom"
            };
        default:
            return { startISO: isoNDaysAgoUtcStart(30), endISO: nowISO, presetId: "30d" };
    }
};

export const defaultDashboardRange = (): DashboardDateRange =>
    resolveStoredRange({ presetId: "30d" });

// localStorage-backed atoms so brand + range survive a full page refresh.
export const dashboardBrandFilterAtom = atomWithStorage<number | null>("dashboard.brandFilter", null);

const dashboardRangeStorageAtom = atomWithStorage<StoredRange>("dashboard.range", { presetId: "30d" });

// Public range atom: reads resolve presets to current dates; writes flatten back to storage shape.
export const dashboardRangeAtom = atom<DashboardDateRange, [DashboardDateRange], void>(
    (get) => resolveStoredRange(get(dashboardRangeStorageAtom)),
    (_get, set, next) => {
        set(dashboardRangeStorageAtom, {
            presetId: next.presetId,
            customStartISO: next.presetId === "custom" ? next.startISO : undefined,
            customEndISO:   next.presetId === "custom" ? next.endISO   : undefined
        });
    }
);

// Compare-to-previous-period toggle. Persisted so the user's preference survives refreshes.
// Defaults to true — the overlay only affects "Orders Over Time" but adds useful context at no
// cost when the period has data.
export const dashboardCompareAtom = atomWithStorage<boolean>("dashboard.compare", true);

export const dashboardPayloadAtom = atom<BrandDashboardPayload | null>(null);
export const globalKPIsAtom = atom<GlobalKPIs | null>(null);
export const recentOrdersAtom = atom<RecentOrder[]>([]);
export const recentDesignsAtom = atom<RecentDesign[]>([]);
export const fulfillmentPipelineAtom = atom<FulfillmentPipelineStage[]>([]);
export const systemHealthAtom = atom<SystemHealthStatus[]>([]);

// One row per brand for the brand-rankings analytics table.
export interface BrandRankingRow {
    brand_id: number;
    brand_name: string;
    total_orders: number;
    total_revenue: number;
    aov: number;
    design_count: number;
    published_design_count: number;
    pending_orders: number;
    completed_orders: number;
    fulfillment_rate: number; // completed / total orders in range, as a percentage (0–100)
    order_trend_percent: number | null;
    revenue_trend_percent: number | null;
}

export const brandRankingsAtom = atom<BrandRankingRow[]>([]);

// Total matching brands across all pages (from the endpoint's total_count) —
// needed so the table can paginate correctly and detect an out-of-range page.
export const brandRankingsTotalAtom = atom<number>(0);

// Per-endpoint last-fetched timestamps (epoch ms) used to show "Updated Xs ago" on each card.
export interface DashboardFetchTimes {
    kpis: number | null;
    globalKpis: number | null;
    recentOrders: number | null;
    recentDesigns: number | null;
    pipeline: number | null;
    systemHealth: number | null;
    brandRankings: number | null;
}

export const dashboardFetchTimesAtom = atom<DashboardFetchTimes>({
    kpis: null,
    globalKpis: null,
    recentOrders: null,
    recentDesigns: null,
    pipeline: null,
    systemHealth: null,
    brandRankings: null
});
